import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const HodViewGrievances = ({ onNavigate, userInfo }) => {

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Grievances data
  const [grievances, setGrievances] = useState([]);
  const [filteredGrievances, setFilteredGrievances] = useState([]);
  const [selectedGrievance, setSelectedGrievance] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Filters and search
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // Reply and action states
  const [replyText, setReplyText] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [actionError, setActionError] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);





  // Fetch grievances data - ENHANCED WITH REAL DATABASE DATA
  useEffect(() => {
    const fetchGrievances = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Fetching HOD grievances data...');

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
          setError('Authentication token not found. Please login again.');
          return;
        }



        // Fetch grievances
        const grievancesResponse = await fetch(`${API_LINK}/api/hod/grievances`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('📡 Grievances response status:', grievancesResponse.status);

        if (!grievancesResponse.ok) {
          if (grievancesResponse.status === 401) {
            setError('Session expired. Please login again.');
            sessionStorage.clear();
            localStorage.clear();
            if (onNavigate) onNavigate('home');
            return;
          } else if (grievancesResponse.status === 404) {
            setError('Grievances API endpoint not found. Please check server configuration.');
            return;
          }
          throw new Error(`HTTP error! status: ${grievancesResponse.status}`);
        }

        const result = await grievancesResponse.json();
        console.log('📦 Grievances API response:', result);

        if (result.success && result.data) {
          // Transform API data to match component expectations
          const transformedGrievances = result.data.map(grievance => ({
            id: grievance.id,
            studentId: grievance.source === 'student' ? grievance.submitterName : null,
            studentName: grievance.submitterName,
            email: grievance.email || 'N/A',
            department: grievance.department,
            year: 'N/A', // Not available in current API response
            subject: grievance.title,
            description: grievance.description,
            category: grievance.category,
            priority: grievance.priority || 'Medium',
            status: grievance.status,
            submittedDate: grievance.submittedDate,
            lastUpdated: grievance.lastUpdated,
            attachments: grievance.attachments.map(att => att.path) || [],
            replies: grievance.responses || [],
            source: grievance.source // 'student' or 'teacher'
          }));

          setGrievances(transformedGrievances);
          setFilteredGrievances(transformedGrievances);
          console.log(`✅ Loaded ${transformedGrievances.length} real grievances from database`);
        } else {
          console.warn('⚠️ No grievances data in API response');
          setGrievances([]);
          setFilteredGrievances([]);
          setError('No grievances found for your department.');
        }

      } catch (error) {
        console.error('💥 Error fetching grievances:', error);
        setError(`Failed to load grievances: ${error.message}. Please check your connection and try again.`);
      } finally {
        setLoading(false);
      }
    };

    fetchGrievances();
  }, [userInfo]);

  // Filter and sort grievances
  useEffect(() => {
    let filtered = [...grievances];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(grievance => grievance.status === statusFilter);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(grievance =>
        grievance.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        grievance.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        grievance.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        grievance.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.submittedDate) - new Date(a.submittedDate);
        case 'oldest':
          return new Date(a.submittedDate) - new Date(b.submittedDate);
        case 'priority':
          const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
          return (priorityOrder[b.priority] || 2) - (priorityOrder[a.priority] || 2);
        case 'status':
          return a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });

    setFilteredGrievances(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [grievances, statusFilter, searchTerm, sortBy]);



  // Handle grievance action (resolve, escalate, etc.)
  const handleGrievanceAction = async (grievanceId, action, additionalData = {}, reviewerName = 'HOD') => {
    try {
      setActionLoading(true);
      setActionError(null);
      setActionSuccess(null);

      console.log(`🔄 Performing action "${action}" on grievance ${grievanceId}`);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        setActionError('Authentication token not found');
        return;
      }

      let requestData = {};
      let apiEndpoint = '';

      // Determine the API endpoint and data based on action
      if (action === 'reply') {
        apiEndpoint = `${API_LINK}/api/hod/grievances/${grievanceId}`;
        requestData = {
          status: 'in-progress',
          response: additionalData.message
        };
      } else if (action === 'resolve') {
        apiEndpoint = `${API_LINK}/api/hod/grievances/${grievanceId}`;
        requestData = {
          status: 'resolved',
          response: additionalData.message || 'Grievance has been resolved.'
        };
      } else if (action === 'in-progress') {
        apiEndpoint = `${API_LINK}/api/hod/grievances/${grievanceId}`;
        requestData = {
          status: 'in-progress',
          response: additionalData.message || 'Grievance is being reviewed.'
        };
      }

      const response = await fetch(apiEndpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Update the grievance in the local state
        setGrievances(prev => prev.map(g => {
          if (g.id === grievanceId) {
            const updatedGrievance = { ...g };
            updatedGrievance.status = requestData.status;
            updatedGrievance.lastUpdated = new Date().toISOString();

            if (requestData.response) {
              const newReply = {
                id: `REP${Date.now()}`,
                author: reviewerName,
                authorType: 'hod',
                message: requestData.response,
                timestamp: new Date().toISOString()
              };
              updatedGrievance.replies = [...(updatedGrievance.replies || []), newReply];
            }

            return updatedGrievance;
          }
          return g;
        }));

        // Update selected grievance if it's the one being acted upon
        if (selectedGrievance && selectedGrievance.id === grievanceId) {
          const updatedGrievance = { ...selectedGrievance };
          updatedGrievance.status = requestData.status;
          updatedGrievance.lastUpdated = new Date().toISOString();

          if (requestData.response) {
            const newReply = {
              id: `REP${Date.now()}`,
              author: hodData.name || 'HOD',
              authorType: 'hod',
              message: requestData.response,
              timestamp: new Date().toISOString()
            };
            updatedGrievance.replies = [...(updatedGrievance.replies || []), newReply];
          }

          setSelectedGrievance(updatedGrievance);
        }

        setActionSuccess(`Action "${action}" completed successfully`);

        // Clear the reply text if it was a reply action
        if (action === 'reply') {
          setReplyText('');
          setIsReplying(false);
        }

        console.log('✅ Grievance action completed successfully');
      } else {
        throw new Error(result.message || 'Action failed');
      }

    } catch (error) {
      console.error('💥 Error performing grievance action:', error);
      setActionError(`Failed to perform action: ${error.message}`);
    } finally {
      setActionLoading(false);

      // Clear success message after 3 seconds
      setTimeout(() => setActionSuccess(null), 3000);
    }
  };

  // Handle reply submission
  const handleReplySubmit = (reviewerName = 'HOD') => {
    if (!replyText.trim()) {
      setActionError('Please enter a reply message');
      return;
    }

    handleGrievanceAction(selectedGrievance.id, 'reply', {
      message: replyText.trim()
    }, reviewerName);
  };



  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Get status color and icon
  const getStatusInfo = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return { color: '#ffc107', bg: '#fff3cd', icon: '⏳', text: 'Pending' };
      case 'in-progress':
        return { color: '#17a2b8', bg: '#d1ecf1', icon: '🔄', text: 'In Progress' };
      case 'resolved':
        return { color: '#28a745', bg: '#d4edda', icon: '✅', text: 'Resolved' };
      case 'escalated':
        return { color: '#dc3545', bg: '#f8d7da', icon: '⬆️', text: 'Escalated' };
      default:
        return { color: '#6c757d', bg: '#e2e3e5', icon: '❓', text: status };
    }
  };

  // Get priority color
  const getPriorityInfo = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return { color: '#dc3545', bg: '#f8d7da', icon: '🔴' };
      case 'medium':
        return { color: '#ffc107', bg: '#fff3cd', icon: '🟡' };
      case 'low':
        return { color: '#28a745', bg: '#d4edda', icon: '🟢' };
      default:
        return { color: '#6c757d', bg: '#e2e3e5', icon: '⚪' };
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentGrievances = filteredGrievances.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredGrievances.length / itemsPerPage);

  return (
    <Layout role="hod" onNavigate={onNavigate} userInfo={userInfo} activePage="hod-grievances">
      {({ hodData, isMobile, handleLogout }) => (
        <>
          {/* Modal CSS */}
          <style>
            {`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 2000;
            padding: 1rem;
            box-sizing: border-box;
          }
          
          .modal-content {
            background: white;
            border-radius: 12px;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
            max-width: 800px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;
            position: relative;
          }
          
          .modal-content::-webkit-scrollbar {
            width: 8px;
          }
          
          .modal-content::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          
          .modal-content::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
          }
          
          .modal-content::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
        `}
          </style>

          {/* Loading Component */}
          {loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 'calc(100vh - 200px)',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid #e3f2fd',
                borderTop: '4px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Grievances...</p>
            </div>
          )}

          {/* Error Component */}
          {error && !loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 'calc(100vh - 200px)',
              flexDirection: 'column',
              gap: '1rem',
              padding: '2rem'
            }}>
              <div style={{
                backgroundColor: '#ffffff',
                padding: '3rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                textAlign: 'center',
                maxWidth: '400px',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{ fontSize: '4rem', color: '#ff6b6b', marginBottom: '1.5rem' }}>⚠️</div>
                <h3 style={{ color: '#333', margin: '0 0 1rem 0', fontSize: '1.4rem', fontWeight: 600 }}>Unable to Load Grievances</h3>
                <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem', lineHeight: 1.5 }}>{error}</p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => window.location.reload()} style={{ backgroundColor: '#238548', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 500 }}>Try Again</button>
                  <button onClick={handleLogout} style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 500 }}>Logout</button>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          {!loading && !error && (
            <>
              {/* Page Header */}
              <div style={{
                background: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem',
                borderLeft: '4px solid #238548'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '1rem'
                }}>
                  <div>
                    <h2 style={{
                      color: '#238548',
                      margin: '0 0 0.5rem 0',
                      fontSize: '1.8rem',
                      fontWeight: 'bold'
                    }}>
                      📋 Student Grievances Management
                    </h2>
                    <p style={{
                      color: '#666',
                      margin: 0,
                      fontSize: '1rem'
                    }}>
                      View, reply to, and manage student grievances for your department ({hodData.department})
                    </p>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{
                      background: '#f8f9fa',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      <strong>Total: {filteredGrievances.length}</strong>
                    </div>

                    <div style={{
                      background: '#fff3cd',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      border: '1px solid #ffeaa7'
                    }}>
                      Pending: {filteredGrievances.filter(g => g.status === 'pending').length}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Messages */}
              {(actionSuccess || actionError) && (
                <div style={{
                  background: actionSuccess ? '#d4edda' : '#f8d7da',
                  border: `1px solid ${actionSuccess ? '#c3e6cb' : '#f5c6cb'}`,
                  color: actionSuccess ? '#155724' : '#721c24',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>
                    {actionSuccess ? '✅' : '❌'}
                  </span>
                  {actionSuccess || actionError}
                </div>
              )}

              {/* Error Display */}
              {error && (
                <div style={{
                  background: '#f8d7da',
                  border: '1px solid #f5c6cb',
                  color: '#721c24',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                  {error}
                </div>
              )}

              {/* Filters and Search */}
              <div style={{
                background: '#ffffff',
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                  gap: '1rem',
                  alignItems: 'end'
                }}>
                  {/* Search */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      🔍 Search Grievances
                    </label>
                    <input
                      type="text"
                      placeholder="Search by subject, student, or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e9ecef';
                      }}
                    />
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Status Filter
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </div>

                  {/* Sort By */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Sort By
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="priority">Priority</option>
                      <option value="status">Status</option>
                    </select>
                  </div>

                  {/* Clear Filters */}
                  <div>
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setStatusFilter('all');
                        setSortBy('newest');
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: '#f8f9fa',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        color: '#333',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#e9ecef';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f8f9fa';
                      }}
                    >
                      🗑️ Clear Filters
                    </button>
                  </div>
                </div>
              </div>

              {/* Grievances List */}
              <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                marginBottom: '2rem'
              }}>
                {currentGrievances.length > 0 ? (
                  <>
                    {currentGrievances.map((grievance, index) => {
                      const statusInfo = getStatusInfo(grievance.status);
                      const priorityInfo = getPriorityInfo(grievance.priority);

                      return (
                        <div
                          key={grievance.id}
                          style={{
                            padding: '1.5rem',
                            borderBottom: index < currentGrievances.length - 1 ? '1px solid #ecf0f1' : 'none',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#ffffff'
                          }}
                          onClick={() => {
                            setSelectedGrievance(grievance);
                            setShowDetailModal(true);
                            setActionError(null);
                            setActionSuccess(null);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#ffffff';
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'start',
                            marginBottom: '1rem',
                            flexWrap: 'wrap',
                            gap: '1rem'
                          }}>
                            <div style={{ flex: 1, minWidth: '200px' }}>
                              <h3 style={{
                                margin: '0 0 0.5rem 0',
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                color: '#333'
                              }}>
                                {grievance.subject}
                              </h3>
                              <p style={{
                                margin: '0 0 0.5rem 0',
                                color: '#666',
                                fontSize: '0.9rem'
                              }}>
                                <strong>{grievance.studentName}</strong> • {grievance.department} • {grievance.source === 'student' ? 'Student' : 'Teacher'}
                              </p>
                              <p style={{
                                margin: 0,
                                color: '#777',
                                fontSize: '0.85rem',
                                lineHeight: 1.4
                              }}>
                                {grievance.description.substring(0, 150)}
                                {grievance.description.length > 150 ? '...' : ''}
                              </p>
                            </div>

                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-end',
                              gap: '0.5rem',
                              minWidth: '150px'
                            }}>
                              <div style={{
                                display: 'flex',
                                gap: '0.5rem',
                                alignItems: 'center'
                              }}>
                                <span style={{
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '15px',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  backgroundColor: statusInfo.bg,
                                  color: statusInfo.color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }}>
                                  {statusInfo.icon} {statusInfo.text}
                                </span>

                                <span style={{
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '15px',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  backgroundColor: priorityInfo.bg,
                                  color: priorityInfo.color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }}>
                                  {priorityInfo.icon} {grievance.priority}
                                </span>
                              </div>

                              <div style={{
                                fontSize: '0.8rem',
                                color: '#999',
                                textAlign: 'right'
                              }}>
                                <div>📅 {formatDate(grievance.submittedDate)}</div>
                                <div>🔄 {formatDate(grievance.lastUpdated)}</div>
                                {grievance.replies && grievance.replies.length > 0 && (
                                  <div style={{ color: '#238548', fontWeight: '500' }}>
                                    💬 {grievance.replies.length} replies
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '0.5rem'
                          }}>
                            <div style={{
                              display: 'flex',
                              gap: '0.5rem',
                              alignItems: 'center',
                              fontSize: '0.85rem',
                              color: '#666'
                            }}>
                              <span>📂 {grievance.category}</span>
                              <span>•</span>
                              <span>👤 {grievance.source === 'student' ? 'Student' : 'Teacher'}</span>
                              {grievance.attachments && grievance.attachments.length > 0 && (
                                <>
                                  <span>•</span>
                                  <span>📎 {grievance.attachments.length} attachments</span>
                                </>
                              )}
                            </div>

                            <div style={{
                              fontSize: '0.9rem',
                              color: '#238548',
                              fontWeight: '500'
                            }}>
                              Click to view details →
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div style={{
                    padding: '3rem',
                    textAlign: 'center',
                    color: '#666'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                    <h3 style={{ margin: '0 0 1rem 0' }}>No Grievances Found</h3>
                    <p style={{ margin: 0 }}>
                      {searchTerm || statusFilter !== 'all'
                        ? 'Try adjusting your search filters'
                        : 'No grievances have been submitted yet for your department'
                      }
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '2rem'
                }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #238548',
                      borderRadius: '8px',
                      background: currentPage === 1 ? '#f8f9fa' : '#238548',
                      color: currentPage === 1 ? '#6c757d' : '#ffffff',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    ← Previous
                  </button>

                  <span style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.9rem',
                    color: '#666'
                  }}>
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #238548',
                      borderRadius: '8px',
                      background: currentPage === totalPages ? '#f8f9fa' : '#238548',
                      color: currentPage === totalPages ? '#6c757d' : '#ffffff',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Next →
                  </button>
                </div>
              )}


              {/* Grievance Detail Modal */}
              {showDetailModal && selectedGrievance && (
                <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
                  <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    {/* Modal Header */}
                    <div style={{
                      background: '#238548',
                      color: '#ffffff',
                      padding: '1.5rem 2rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 'bold' }}>
                        📋 Grievance Details
                      </h2>
                      <button
                        onClick={() => setShowDetailModal(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ffffff',
                          fontSize: '1.5rem',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          borderRadius: '4px',
                          lineHeight: 1
                        }}
                      >
                        ✕
                      </button>
                    </div>

                    {/* Modal Content */}
                    <div style={{ padding: '2rem' }}>
                      {/* Grievance Info */}
                      <div style={{
                        background: '#f8f9fa',
                        padding: '1.5rem',
                        borderRadius: '8px',
                        marginBottom: '1.5rem',
                        border: '1px solid #e9ecef'
                      }}>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                          gap: '1rem',
                          marginBottom: '1rem'
                        }}>
                          <div>
                            <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>
                              {selectedGrievance.subject}
                            </h3>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Name:</strong> {selectedGrievance.studentName}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Type:</strong> {selectedGrievance.source === 'student' ? 'Student' : 'Teacher'}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Email:</strong> {selectedGrievance.email}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Department:</strong> {selectedGrievance.department}
                            </div>
                          </div>

                          <div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Category:</strong> {selectedGrievance.category}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Priority:</strong>
                              <span style={{
                                marginLeft: '0.5rem',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                ...getPriorityInfo(selectedGrievance.priority)
                              }}>
                                {getPriorityInfo(selectedGrievance.priority).icon} {selectedGrievance.priority}
                              </span>
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Status:</strong>
                              <span style={{
                                marginLeft: '0.5rem',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                backgroundColor: getStatusInfo(selectedGrievance.status).bg,
                                color: getStatusInfo(selectedGrievance.status).color
                              }}>
                                {getStatusInfo(selectedGrievance.status).icon} {getStatusInfo(selectedGrievance.status).text}
                              </span>
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Submitted:</strong> {formatDate(selectedGrievance.submittedDate)}
                            </div>
                            <div>
                              <strong>Last Updated:</strong> {formatDate(selectedGrievance.lastUpdated)}
                            </div>
                          </div>
                        </div>

                        <div>
                          <strong>Description:</strong>
                          <p style={{
                            margin: '0.5rem 0 0 0',
                            padding: '1rem',
                            background: '#ffffff',
                            borderRadius: '6px',
                            border: '1px solid #e9ecef',
                            lineHeight: 1.6
                          }}>
                            {selectedGrievance.description}
                          </p>
                        </div>

                        {/* Attachments */}
                        {selectedGrievance.attachments && selectedGrievance.attachments.length > 0 && (
                          <div style={{ marginTop: '1rem' }}>
                            <strong>Attachments:</strong>
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '0.5rem',
                              marginTop: '0.5rem'
                            }}>
                              {selectedGrievance.attachments.map((attachment, index) => (
                                <span
                                  key={index}
                                  style={{
                                    padding: '0.25rem 0.75rem',
                                    background: '#e9ecef',
                                    borderRadius: '15px',
                                    fontSize: '0.8rem',
                                    color: '#495057'
                                  }}
                                >
                                  📎 {attachment}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      {selectedGrievance.status !== 'resolved' && (
                        <div style={{
                          display: 'flex',
                          gap: '1rem',
                          marginBottom: '1.5rem',
                          flexWrap: 'wrap'
                        }}>
                          <button
                            onClick={() => handleGrievanceAction(selectedGrievance.id, 'in-progress', {}, hodData?.name)}
                            disabled={actionLoading || selectedGrievance.status === 'in-progress'}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: selectedGrievance.status === 'in-progress' ? '#6c757d' : '#17a2b8',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: selectedGrievance.status === 'in-progress' ? 'not-allowed' : 'pointer',
                              fontWeight: '500',
                              opacity: actionLoading ? 0.7 : 1
                            }}
                          >
                            🔄 Mark In Progress
                          </button>

                          <button
                            onClick={() => handleGrievanceAction(selectedGrievance.id, 'resolve', {}, hodData?.name)}
                            disabled={actionLoading}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: '#28a745',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              opacity: actionLoading ? 0.7 : 1
                            }}
                          >
                            ✅ Mark Resolved
                          </button>

                          <button
                            onClick={() => setIsReplying(!isReplying)}
                            disabled={actionLoading}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: isReplying ? '#dc3545' : '#238548',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              opacity: actionLoading ? 0.7 : 1
                            }}
                          >
                            {isReplying ? '❌ Cancel Reply' : '💬 Reply'}
                          </button>
                        </div>
                      )}

                      {/* Reply Section */}
                      {isReplying && (
                        <div style={{
                          background: '#f8f9fa',
                          padding: '1.5rem',
                          borderRadius: '8px',
                          marginBottom: '1.5rem',
                          border: '1px solid #e9ecef'
                        }}>
                          <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>💬 Reply to {selectedGrievance.source === 'student' ? 'Student' : 'Teacher'}</h4>
                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Type your reply here..."
                            rows={4}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '2px solid #e9ecef',
                              borderRadius: '8px',
                              fontSize: '0.95rem',
                              resize: 'vertical',
                              marginBottom: '1rem'
                            }}
                          />
                          <button
                            onClick={() => handleReplySubmit(hodData?.name)}
                            disabled={actionLoading || !replyText.trim()}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: !replyText.trim() ? '#6c757d' : '#238548',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: !replyText.trim() ? 'not-allowed' : 'pointer',
                              fontWeight: '500',
                              opacity: actionLoading ? 0.7 : 1
                            }}
                          >
                            {actionLoading ? '⏳ Sending...' : '📤 Send Reply'}
                          </button>
                        </div>
                      )}

                      {/* Replies History */}
                      {selectedGrievance.replies && selectedGrievance.replies.length > 0 && (
                        <div>
                          <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>💬 Conversation History</h4>
                          <div style={{
                            maxHeight: '300px',
                            overflowY: 'auto',
                            border: '1px solid #e9ecef',
                            borderRadius: '8px'
                          }}>
                            {selectedGrievance.replies.map((reply, index) => (
                              <div
                                key={reply.id || index}
                                style={{
                                  padding: '1rem',
                                  borderBottom: index < selectedGrievance.replies.length - 1 ? '1px solid #f1f3f4' : 'none',
                                  backgroundColor: reply.authorType === 'hod' ? '#e8f5e8' : '#f8f9fa'
                                }}
                              >
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom: '0.5rem'
                                }}>
                                  <div style={{
                                    fontWeight: 'bold',
                                    color: reply.authorType === 'hod' ? '#238548' : '#495057',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                  }}>
                                    {reply.authorType === 'hod' ? '👤' : '🏢'} {reply.author || reply.responder}
                                    <span style={{
                                      fontSize: '0.7rem',
                                      padding: '0.2rem 0.5rem',
                                      borderRadius: '10px',
                                      backgroundColor: reply.authorType === 'hod' ? '#238548' : '#6c757d',
                                      color: '#ffffff'
                                    }}>
                                      {reply.authorType === 'hod' ? 'HOD' : 'ADMIN'}
                                    </span>
                                  </div>
                                  <span style={{
                                    fontSize: '0.8rem',
                                    color: '#6c757d'
                                  }}>
                                    {formatDate(reply.timestamp || reply.date)}
                                  </span>
                                </div>
                                <p style={{
                                  margin: 0,
                                  color: '#495057',
                                  lineHeight: 1.5
                                }}>
                                  {reply.message}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </>
          )}
        </>
      )}
    </Layout>
  );
};

export default HodViewGrievances;